import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  computed,
  signal,
  effect,
  OnDestroy,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { SoButtonGradientUI } from '../so-button-gradient/so-button-gradient.component';

export interface AlertButton {
  label: string;
  action: () => void;
  color?: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'gray';
  variant?: 'solid' | 'outline' | 'gradient';
  disabled?: boolean;
  requiresConfirmMatch?: boolean;
}

export type AlertType = 'info' | 'success' | 'warning' | 'danger';

let nextUniqueId = 0;

@Component({
  selector: 'so-alert-ui',
  standalone: true,
  imports: [CommonModule, SoButtonGradientUI],
  templateUrl: './so-alert.component.html',
  styleUrl: './so-alert.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SoAlertUI implements OnDestroy {
  @ViewChild('alertTemplate', { static: true }) alertTemplate!: TemplateRef<any>;
  // Inputs
  title = input<string>('');
  content = input<string>('');
  type = input<AlertType>('info');
  buttons = input<AlertButton[]>([]);
  showCloseButton = input<boolean>(true);
  icon = input<string>('');
  showIcon = input<boolean>(true);
  isOpen = input<boolean>(false);
  closeOnBackdrop = input<boolean>(true);
  autoClose = input<number>(0);
  confirmText = input<string | undefined>(undefined);
  confirmPlaceholder = input<string | undefined>(undefined);

  // Outputs
  closed = output<void>();

  // Internal state
  protected isClosing = signal<boolean>(false);
  protected typedValue = signal<string>('');
  protected readonly confirmInputId = `so-alert-confirm-input-${nextUniqueId++}`;
  private autoCloseTimer: any = null;
  private overlayRef: OverlayRef | null = null;
  private portal: TemplatePortal | null = null;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef
  ) {
    effect(() => {
      if (this.isOpen()) {
        this.showOverlay();
        if (this.autoClose() > 0) {
          this.startAutoCloseTimer();
        }
      } else {
        this.clearAutoCloseTimer();
        this.hideOverlay();
        this.typedValue.set('');
      }
    }, {  });
  }

  ngOnDestroy(): void {
    this.hideOverlay();
    this.clearAutoCloseTimer();
  }

  // Computed properties
  protected alertClasses = computed(() => ({
    'so-alert-modal': true,
    [`so-alert-modal--${this.type()}`]: true,
    'so-alert-modal--closing': this.isClosing()
  }));

  protected defaultIcon = computed(() => {
    if (this.icon()) return this.icon();

    const iconMap: Record<AlertType, string> = {
      info: 'info',
      success: 'check_circle',
      warning: 'warning',
      danger: 'error'
    };

    return iconMap[this.type()];
  });

  protected hasButtons = computed(() => this.buttons().length > 0);

  protected hasConfirmInput = computed(() => this.confirmText() !== undefined);

  protected confirmLabel = computed(() => `Escribe "${this.confirmText()}" para confirmar`);

  // Methods
  private showOverlay(): void {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        hasBackdrop: false,
        positionStrategy: this.overlay.position().global(),
        scrollStrategy: this.overlay.scrollStrategies.block()
      });
    }

    if (!this.portal) {
      this.portal = new TemplatePortal(
        this.alertTemplate,
        this.viewContainerRef
      );
    }

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(this.portal);
    }
  }

  private hideOverlay(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  private startAutoCloseTimer(): void {
    this.clearAutoCloseTimer();
    this.autoCloseTimer = setTimeout(() => {
      this.close();
    }, this.autoClose());
  }

  private clearAutoCloseTimer(): void {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
  }

  close(): void {
    this.clearAutoCloseTimer();
    this.isClosing.set(true);
    setTimeout(() => {
      this.isClosing.set(false);
      this.closed.emit();
    }, 200);
  }

  onBackdropClick(): void {
    if (this.closeOnBackdrop()) {
      this.close();
    }
  }

  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  onConfirmInputChange(value: string): void {
    this.typedValue.set(value);
  }

  protected isConfirmLocked(button: AlertButton): boolean {
    if (!button.requiresConfirmMatch) return false;

    const expected = this.confirmText();
    if (expected === undefined) return false;

    return this.typedValue() !== expected;
  }

  handleButtonClick(button: AlertButton): void {
    if (button.disabled || this.isConfirmLocked(button)) return;
    button.action();
  }

  getButtonColor(variant?: string): 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'gray' {
    const colorMap: Record<string, 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'gray'> = {
      primary: 'primary',
      secondary: 'secondary',
      danger: 'danger',
      success: 'success',
      warning: 'warning',
      gray: 'gray'
    };
    return colorMap[variant || 'primary'] || 'primary';
  }
}